// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/mission-execution/TaskIntelligencePanel.jsx

import React from "react";
import {
  Alert,
  Button,
  Card,
  Section,
  Skeleton,
  StatusChip,
} from "../../design-system/components";

const DEFAULT_QUESTION = "Why is OASYS telling me to do this first?";

const SUGGESTED_QUESTIONS = [
  "Why is this task first?",
  "Walk me through the fix.",
  "What could block validation?",
  "What should I verify before starting?",
];

const SOURCE_LABELS = {
  selected_work_item: "Selected Task",
  execution_priority: "Execution Priority",
  execution_control_context: "Control Context",
  recommendation_context: "Recommendation Context",
  remediation_overview: "Remediation Overview",
  remediation_tasks: "Remediation Tasks",
  open_remediation_tasks: "Open Remediation Tasks",
  active_workflows: "Active Workflow",
  workflow_overview: "Workflow Overview",
  workflow_steps: "Workflow Steps",
  workflow_events: "Workflow History",
  related_workflows: "Related Workflows",
  related_findings: "Related Findings",
  related_evidence: "Task Evidence",
  evidence_summary: "Evidence Summary",
  evidence_readiness: "Evidence Readiness",
  recent_evidence: "Recent Evidence",
  validation_overview: "Validation Overview",
  focused_validation: "Selected Validation",
  failed_validations: "Failed Validations",
  recent_validation_runs: "Validation History",
  validation_runs: "Validation Activity",
  decision_support: "Decision Support",
  learning_context: "Organizational Learning",
  sop_overview: "SOP Overview",
  linked_sops: "Linked SOPs",
  priority_actions: "Priority Actions",
};

function clean(value) {
  const text = String(value ?? "").trim();
  return text || "";
}

function titleCaseSource(value) {
  return clean(value)
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function normalizeConfidence(value) {
  const normalized = clean(value).toLowerCase();
  if (normalized === "high") return { label: "High confidence", tone: "success" };
  if (normalized === "low") return { label: "Low confidence", tone: "warning" };
  if (normalized === "medium") return { label: "Medium confidence", tone: "informational" };
  return { label: "Advisory", tone: "neutral" };
}

function sourceLabel(source) {
  if (!source || typeof source !== "object") return "OASYS Context";

  const sourceId = clean(source.id || source.type);
  const explicitLabel = clean(source.item_label || source.label);

  if (explicitLabel) {
    return explicitLabel
      .replace(/\bai\b/gi, "AI")
      .replace(/\bsop\b/gi, "SOP")
      .replace(/\bdlq\b/gi, "DLQ")
      .replace(/^./, (letter) => letter.toUpperCase());
  }

  return SOURCE_LABELS[sourceId] || titleCaseSource(sourceId) || "OASYS Context";
}

function sourceDetail(source) {
  if (!source || typeof source !== "object") return "";
  const represented = Number(source.item_count);
  const records = Number(source.records);

  if (Number.isFinite(represented) && represented > 0) {
    return `${represented} represented`;
  }
  if (Number.isFinite(records) && records > 0) {
    return `${records} loaded`;
  }
  return "";
}

export default function TaskIntelligencePanel({
  intelligence = null,
  loading = false,
  error = "",
  activeQuestion = "",
  onRequest,
}) {
  const card = intelligence?.card && typeof intelligence.card === "object"
    ? intelligence.card
    : {};

  const summary = clean(card.summary);
  const reasoning = clean(card.reasoning || card.explanation);
  const isRemediationWalkthrough = clean(activeQuestion) === "Walk me through the fix.";
  const availableActions = Array.isArray(card.recommended_actions)
    ? card.recommended_actions.filter(Boolean)
    : [];
  const actions = isRemediationWalkthrough
    ? availableActions
    : availableActions.slice(0, 4);
  const limitations = Array.isArray(card.known_limitations)
    ? card.known_limitations.filter(Boolean)
    : Array.isArray(card.limitations)
      ? card.limitations.filter(Boolean)
      : [];
  const evidence = Array.isArray(card.evidence_used)
    ? card.evidence_used.filter(Boolean)
    : [];
  const metadata = card.metadata && typeof card.metadata === "object"
    ? card.metadata
    : intelligence?.metadata && typeof intelligence.metadata === "object"
      ? intelligence.metadata
      : {};

  const confidence = normalizeConfidence(card.confidence);
  const sourceCount = Number(metadata.context_source_count);
  const displaySourceCount = Number.isFinite(sourceCount) && sourceCount > 0
    ? sourceCount
    : evidence.length;

  const ask = (question) => {
    if (loading || typeof onRequest !== "function") return;
    onRequest(question);
  };

  const hasResponse = Boolean(summary || reasoning || actions.length || evidence.length);

  return (
    <Section
      eyebrow="OASYS Intelligence"
      title="Why this work matters now"
      description="Grounded, advisory guidance for the selected task."
      badge={confidence.label}
      badgeTone={confidence.tone}
      actions={
        <Button
          size="small"
          variant="secondary"
          disabled={loading}
          onClick={() => ask(activeQuestion || DEFAULT_QUESTION)}
          className="task-intelligence-panel__refresh"
        >
          Refresh reasoning
        </Button>
      }
      className="task-intelligence-panel"
    >
      {error ? (
        <Alert title="Intelligence unavailable" tone="warning">
          {error}
        </Alert>
      ) : null}

      {loading && !hasResponse ? (
        <div className="task-intelligence-panel__loading" aria-label="Loading OASYS Intelligence">
          <Skeleton height="20px" width="72%" label="Loading intelligence summary" />
          <Skeleton height="14px" width="100%" label="Loading intelligence reasoning" />
          <Skeleton height="14px" width="88%" label="Loading intelligence reasoning" />
          <Skeleton height="84px" width="100%" radius="var(--oasys-radius-card)" label="Loading recommended actions" />
        </div>
      ) : null}

      {hasResponse ? (
        <div className="task-intelligence-panel__content">
          <Card tone="secondary" density="compact" className="task-intelligence-panel__ask">
            <span className="task-intelligence-panel__label">Ask OASYS about this task</span>
            <div className="task-intelligence-panel__prompts">
              {SUGGESTED_QUESTIONS.map((question) => {
                const isActive = clean(activeQuestion) === question;

                return (
                  <Button
                    key={question}
                    size="small"
                    variant={isActive ? "primary" : "secondary"}
                    disabled={loading}
                    aria-pressed={isActive}
                    onClick={() => ask(question)}
                    className={`task-intelligence-panel__prompt${
                      isActive ? " is-active" : ""
                    }`}
                  >
                    {question}
                  </Button>
                );
              })}
            </div>
          </Card>

          <Card tone="emphasis" density="default" className="task-intelligence-panel__summary">
            <span className="task-intelligence-panel__label">Current reasoning</span>
            <h3>{summary || "OASYS Intelligence has task context available."}</h3>
            {reasoning ? <p>{reasoning}</p> : null}
          </Card>

          {actions.length ? (
            <Card tone="secondary" density="default" className="task-intelligence-panel__actions">
              <span className="task-intelligence-panel__label">Recommended operator path</span>
              <ol>
                {actions.map((action, index) => (
                  <li key={`intelligence-action-${index}`}>{action}</li>
                ))}
              </ol>
            </Card>
          ) : null}

          <div className="task-intelligence-panel__disclosures">
            <Card
              as="details"
              tone="secondary"
              density="compact"
              className="task-intelligence-panel__disclosure"
            >
              <summary>
                <div>
                  <span className="task-intelligence-panel__label">Grounding</span>
                  <strong>
                    {displaySourceCount > 0
                      ? `Grounded in ${displaySourceCount} OASYS source${displaySourceCount === 1 ? "" : "s"}`
                      : "Grounded in OASYS task context"}
                  </strong>
                </div>
                <StatusChip tone="informational" presentation="outline">
                  Grounded
                </StatusChip>
              </summary>

              {evidence.length ? (
                <div className="task-intelligence-panel__sources">
                  {evidence.map((source, index) => (
                    <div
                      key={`${clean(source?.id || source?.type) || "source"}-${index}`}
                      className="task-intelligence-panel__source"
                    >
                      <span>{sourceLabel(source)}</span>
                      {sourceDetail(source) ? <small>{sourceDetail(source)}</small> : null}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="task-intelligence-panel__muted">
                  Supporting OASYS records are attached to the current response.
                </p>
              )}
            </Card>

            {limitations.length ? (
              <Card
                as="details"
                tone="secondary"
                density="compact"
                className="task-intelligence-panel__disclosure"
              >
                <summary>
                  <div>
                    <span className="task-intelligence-panel__label">Transparency</span>
                    <strong>
                      {limitations.length} known limitation{limitations.length === 1 ? "" : "s"}
                    </strong>
                  </div>
                  <StatusChip tone="neutral" presentation="outline">
                    Review
                  </StatusChip>
                </summary>

                <ul className="task-intelligence-panel__limitations">
                  {limitations.map((limitation, index) => (
                    <li key={`intelligence-limitation-${index}`}>{limitation}</li>
                  ))}
                </ul>
              </Card>
            ) : null}
          </div>
        </div>
      ) : null}

      {!loading && !error && !hasResponse ? (
        <Alert title="Intelligence context is ready" tone="neutral">
          Select a suggested question to generate grounded reasoning for this task.
        </Alert>
      ) : null}
    </Section>
  );
}
